import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  CircularProgress,
  Hidden,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';

import services, { useGetCustomerCartsQuery } from 'core/services';
import useIsLoading from 'hooks/useIsLoading.hook';
import { getArray } from 'utils/helpers';
import Accent from 'components/Accent';
import InlinePrice from 'components/InlinePrice';
import Text from 'components/Text';
import TextLabel from 'components/TextLabel';

const { changeCart } = services;

const menuPropsDefault = {
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    borderRadius: theme.spacing(0, 0.5, 0, 0),
    paddingLeft: theme.spacing(1),
    position: 'relative',

    '&:has(.Mui-focused), &:hover': {
      backgroundColor: theme.palette.colors.grey200,

      '& $accent': {
        color: theme.palette.primary.dark,
      },
    },
    '& $accent': {
      color: theme.palette.primary.main,
    },
  },
  accent: {},
  label: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  loadingLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  select: {
    height: 40,
  },
  menu: {
    margin: theme.spacing(1, 0),

    '& .MuiList-root': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.2),
    },
  },
  listItem: {
    padding: 0,
    // Overwrite global styles (from theme.js)
    minHeight: 'auto !important',
    textTransform: 'initial',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
    gap: theme.spacing(2),

    '&.Mui-selected': {
      backgroundColor: theme.palette.colors.grey100,

      '& $itemAccent': {
        color: theme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: theme.palette.colors.grey200,

        '& $itemAccent': {
          color: theme.palette.primary.dark,
        },
      },
    },
    '& $itemAccent': {
      color: theme.palette.colors.grey300,
    },
    '&:hover': {
      '&.MuiListItem-root': {
        backgroundColor: theme.palette.colors.grey100,
      },

      '& $itemAccent': {
        color: theme.palette.secondary.main,
      },
    },
  },
  itemAccent: {},

  listItemLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'relative',
    padding: theme.spacing(1),
  },
  listItemMeta: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const CartSelect = () => {
  const classes = useStyles();
  const cart_id = useSelector((state) => state.cart.cart_id);
  const cartsQuery = useGetCustomerCartsQuery();
  const carts = getArray(cartsQuery.data);
  const loading = useIsLoading(cartsQuery, !cart_id);
  const value = cart_id || 'loading';

  const handleChange = useCallback((e) => {
    changeCart(e.target.value);
  }, []);

  return (
    <div className={classes.root}>
      <Accent className={classes.accent} />

      <Select
        fullWidth
        value={value}
        disabled={loading}
        className={classes.select}
        onChange={handleChange}
        MenuProps={{
          ...menuPropsDefault,
          className: classes.menu,
        }}
        renderValue={(id) => {
          if (id === 'loading') {
            return (
              <div className={classes.loadingLabel}>
                <span>Loading</span>
                <CircularProgress size={20} />
              </div>
            );
          }
          const opt = carts.find((c) => c.id === id);

          return (
            <div className={classes.label}>
              <Text variant="title5-bold" color="colors.grey900">
                {opt?.title?.toUpperCase()}
              </Text>
            </div>
          );
        }}
      >
        {carts.map((c) => {
          const itemsLabel = c.total_item === 1 ? 'item' : 'items';

          return (
            <MenuItem className={classes.listItem} key={c.id} value={c.id}>
              <div className={classes.listItemLabel}>
                <Accent className={classes.itemAccent} />

                <Text variant="body1-bold" color="colors.grey800">
                  {c.title?.toUpperCase()}
                </Text>

                {c.lab_group && (
                  <Hidden xsDown>
                    <TextLabel>Shared Cart</TextLabel>
                  </Hidden>
                )}
              </div>

              <div className={classes.listItemMeta}>
                <Text mr={1} color="colors.grey500" variant="subtitle3">
                  {c.total_items} {itemsLabel}, total
                </Text>

                <InlinePrice color="colors.grey500" value={c.total_price} />
              </div>
            </MenuItem>
          );
        })}
      </Select>
    </div>
  );
};

export default CartSelect;
